import { config as _config } from 'CONFIG';
import user, { ACCESS_TOKEN_EXPIRES_KEY, ACCESS_TOKEN_KEY } from '@/api/user';
import store from '@/store';
import { delay, setToClient } from '@/common/js/utils';
import { requestToApp } from '@/api/app';
import md5 from '@/common/js/md5';
import { AxiosError, AxiosResponse } from 'axios';
import { IRequestConfig } from '../interface';
import { HttpClient } from '../client';

/**
 * http设置相关
 * axios相关
 * 参考https: //github.com/axios/axios/blob/master/README.md
 */

// 动态获取router，避免循环依赖
function getCurrentRouteRequiresAuth(): boolean | undefined {

    return ( window as any ).__CURRENT_ROUTE_REQUIRES_AUTH__;

}

function genRandomString( length: number ): string {

    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';

    for ( let i = length; i > 0; --i ) {

        result += chars[Math.floor( Math.random() * chars.length )];

    }

    return result;

}

function genPageRequestId( location ): string {

    return `${new Date().toISOString().replace( /\D/g, '' ).slice( 2 )}-${md5( location ).slice( 0, 8 )}-${genRandomString( 7 )}`;

}

export class RequireAuth {

    private readonly _defaults;

    private readonly frequency;

    private __PRID__;

    private getTokenPromise: Promise<void> | null = null;

    public constructor ( defaults ) {

        this._defaults = defaults;
        this.frequency = {

            approvePayment: {

                value: 0.5

            },
            cancelOwnedSubscription: {

                value: 0.5

            },
            cancelPayment: {

                value: 0.5

            },
            createPayment: {

                value: 0.25

            },
            createOrder: {

                value: 0.2

            },
            previewCancelOwnedSubscription: {

                value: 1

            },
            previewSubscription: {

                value: 1

            }

        };
        this.__PRID__ = '';

    }

    /**
     * 单例模式获取token，避免多次并发调用导致callback被覆盖
     */
    private getTokenFromApp( config: IRequestConfig ): Promise<void> {

        // 如果已经有正在进行的getToken请求，直接返回该Promise
        if ( this.getTokenPromise ) {

            return this.getTokenPromise;

        }

        // 创建新的getToken请求
        this.getTokenPromise = new Promise<void>(( resolve, reject ) => {

            // 先刷新token
            requestToApp( 'refreshToken', undefined, () => {

                // 然后获取新token
                requestToApp( 'getToken', undefined, function ( tokenData ) {

                    try {

                        const { token, expiresIn = ( new Date()).getTime() + 1500 * 100 } = JSON.parse( tokenData );

                        user.setValue( 'accessToken', token );
                        user.setValue( 'tokenExpires', expiresIn );
                        const currentDate = new Date( expiresIn );

                        setToClient( ACCESS_TOKEN_KEY, token, currentDate.getTime());
                        setToClient( ACCESS_TOKEN_EXPIRES_KEY, expiresIn.toString(), currentDate.getTime());
                        user.refreshHeader();
                        config.headers.Authorization = `Bearer ${token}`;

                        resolve();

                    } catch ( err ) {

                        reject( err );

                    }

                });

            });

        }).finally(() => {

            // 请求完成后清空Promise缓存，允许下次调用
            this.getTokenPromise = null;

        });

        return this.getTokenPromise;

    }

    public async requestInterceptor( config: IRequestConfig ): Promise<IRequestConfig> {

        if ( _config.client !== 'IOS' && _config.client !== 'Android' ) {

            const requiresAuth = getCurrentRouteRequiresAuth();

            /**
             * 如果没有登录且当前页面需要登录，则跳转到登录页面
             */

            if ( !user.accuontCenterIsLogined() && requiresAuth ) {

                await user.goToAccountCenter( requiresAuth );

            }

            /**
             * 如果token过期了，则先刷新token
             */

            if ( !config.url?.includes( '/oauth2/token/' ) && config.headers.Authorization && !user.tokenValid()) {

                try {

                    await user.getToken();
                    user.refreshHeader();

                    // details接口特殊处理
                    if ( !config.url?.includes( '/oauth2/session/details/' )) {

                        config.headers.Authorization = this._defaults.headers.Authorization;

                    }

                } catch ( err ) {

                    console.error( err );

                }

            }

        } else {

            if ( !config.url?.includes( '/oauth2/token/' ) && config.headers.Authorization && !user.tokenValid()) {

                /** 刷新token，使用单例模式避免多次并发调用导致callback被覆盖 */
                await this.getTokenFromApp( config );

            }

        }

        /**
         * reolink 的接口不接受Authorization请求头
         */
        if ( config.baseURL?.includes( _config.reolink )) {

            delete config.headers.Authorization;

        }

        /**
         * 接口调用频率控制
         */
        const frequencyKey = config.frequency;
        if ( frequencyKey && this.frequency[frequencyKey] ) {

            const { lastTriggerAt = 0, value } = this.frequency[frequencyKey];
            const now = new Date().getTime();
            const dis = lastTriggerAt + ( 1000 / value ) - now;
            if ( dis > 0 ) {

                await delay( dis );

            }

        }

        if ( !this.__PRID__ ) {

            this.__PRID__ = genPageRequestId( config.url );

        }

        config.headers['X-PRID'] = this.__PRID__;

        return config;

    }

    public requestOnError( err: string ): void {

        console.error( `err ${err}` );

    }

    /**
     *
     * @param {import('axios').AxiosResponse} response
     */
    public responseInterceptor( response ): AxiosResponse {

        const frequencyKey = response.config.frequency;
        if ( frequencyKey && this.frequency[frequencyKey] ) {

            this.frequency[frequencyKey].lastTriggerAt = new Date().getTime();

        }

        return response;

    }

    public async responseOnError( err: AxiosError ): Promise<void> {

        if ( err.response ) {

            try {

                const code = err.response.data.error.code;
                const symbol = err.response.data.error.symbol;
                const requiresAuth = getCurrentRouteRequiresAuth();
                /**
                 * 8213: forced_logout
                 * 12308: error when get token (Auth header invalid)
                 */
                if ( _config.platform === 'web' ) {

                    if ( code === 12308 && requiresAuth ) {

                        user.clearToken();
                        await user.goToAccountCenter( requiresAuth );

                    } else if ( code === 8213 && requiresAuth ) {

                        await user.getToken();
                        user.refreshHeader();
                        store.commit( 'LOGOUT_FORCED', true );

                    }

                } else {

                    if (
                        code === 12308 ||
                        code === 8213 ||
                        symbol === 'token_expired'
                    ) {

                        // store.commit( 'LOGOUT_FORCED', true );
                        const refreshAppToken = ( data ) => {

                            console.log( 'refresh app token' );
                            data = JSON.parse( data );
                            user.setValue( 'accessToken', data.token );
                            user.refreshHeader();

                        };
                        requestToApp( 'refreshToken', undefined, refreshAppToken );

                    }

                }

            } catch ( err ) {

                console.error( err );

            }

            return Promise.reject( err );

        }

        return Promise.reject( err );

    }

    public static register( httpClient: HttpClient ): void {

        const plugin = new RequireAuth( httpClient._defaults );
        httpClient._client.interceptors.request.use(
            plugin.requestInterceptor.bind( plugin ),
            plugin.requestOnError.bind( plugin )
        );

        httpClient._client.interceptors.response.use(
            plugin.responseInterceptor.bind( plugin ),
            plugin.responseOnError.bind( plugin )
        );

    }

}
